import React, { useState, useEffect, useMemo } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, IconButton, Grid, 
    Chip, Divider
} from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";

export default function ProductOptionsModal({ open, onClose, product, onAddToCart }) {
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectedModifiers, setSelectedModifiers] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [currentPrice, setCurrentPrice] = useState(0);

    // 1. Agrupar variantes por Atributo (ej. "Color": ["Rojo", "Azul"])
    const optionsMap = useMemo(() => {
        const options = {};
        if (!product || !product.variants) return options;
        
        product.variants.forEach(variant => {
            if (variant.attributes && Array.isArray(variant.attributes)) {
                variant.attributes.forEach(attr => {
                    if (!options[attr.name]) options[attr.name] = new Set();
                    options[attr.name].add(attr.value);
                });
            }
        });
        
        Object.keys(options).forEach(key => options[key] = Array.from(options[key]));
        return options;
    }, [product]);

    // 2. Seleccionar la primera opción por defecto al abrir
    useEffect(() => {
        if (product && open) {
            setQuantity(1);
            const defaultOptions = {};
            Object.keys(optionsMap).forEach(key => {
                if (optionsMap[key].length > 0) defaultOptions[key] = optionsMap[key][0];
            });
            setSelectedOptions(defaultOptions);
            setSelectedModifiers({});
        }
    }, [product, optionsMap, open]);

    // 3. Calcular Precio Final
    useEffect(() => {
        if (!product) return;
        let basePrice = product.price_from || product.price || 0;
        let extrasPrice = 0;

        // Buscar variante exacta si todas las opciones están seleccionadas
        if (product.variants && Object.keys(optionsMap).length > 0) {
            const matchedVariant = product.variants.find(v => 
                v.attributes.every(attr => selectedOptions[attr.name] === attr.value)
            );
            if (matchedVariant) basePrice = parseFloat(matchedVariant.price);
        }

        // Sumar extras (Modificadores)
        if (product.modifier_groups) {
            product.modifier_groups.forEach(group => {
                const selection = selectedModifiers[group.id];
                if (!selection) return;

                if (group.selection_type === 'single') {
                    const opt = group.options.find(o => o.id === selection);
                    if (opt) extrasPrice += parseFloat(opt.price);
                } else {
                    selection.forEach(optId => {
                        const opt = group.options.find(o => o.id === optId);
                        if (opt) extrasPrice += parseFloat(opt.price);
                    });
                }
            });
        }

        setCurrentPrice(basePrice + extrasPrice);
    }, [selectedOptions, selectedModifiers, product, optionsMap]);

    const handleOptionSelect = (name, value) => {
        setSelectedOptions(prev => ({ ...prev, [name]: value }));
    };

    const handleModifierToggle = (groupId, optionId, type) => {
        setSelectedModifiers(prev => {
            const current = prev[groupId];
            if (type === 'single') {
                return { ...prev, [groupId]: current === optionId ? null : optionId };
            } else {
                const arr = current || [];
                const exists = arr.includes(optionId);
                return { 
                    ...prev, 
                    [groupId]: exists ? arr.filter(id => id !== optionId) : [...arr, optionId] 
                };
            }
        });
    };

    if (!product) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant="h6" fontWeight="700" color="#0F172A">{product.name}</Typography>
                <IconButton onClick={onClose} size="small"><FontAwesomeIcon icon={faClose}/></IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
                {/* VARIANTES */}
                {Object.keys(optionsMap).map(optionName => (
                    <Box key={optionName} mb={3}>
                        <Typography variant="subtitle2" fontWeight="700" color="text.secondary" mb={1} textTransform="uppercase">
                            {optionName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {optionsMap[optionName].map(val => (
                                <Chip 
                                    key={val} 
                                    label={val} 
                                    onClick={() => handleOptionSelect(optionName, val)}
                                    sx={{ 
                                        borderRadius: 2, px: 1, py: 2.5, fontWeight: '600', fontSize: '1rem',
                                        bgcolor: selectedOptions[optionName] === val ? '#0F172A' : 'white',
                                        color: selectedOptions[optionName] === val ? 'white' : '#475569',
                                        border: selectedOptions[optionName] === val ? 'none' : '1px solid #cbd5e1'
                                    }} 
                                />
                            ))}
                        </Box>
                    </Box>
                ))}

                {/* MODIFICADORES */}
                {product.modifier_groups?.map(group => (
                    <Box key={group.id} mb={3}>
                        <Typography variant="subtitle2" fontWeight="700" color="text.secondary" mb={1} textTransform="uppercase">
                            {group.name} {group.required ? '*' : ''}
                        </Typography>
                        <Grid container spacing={1}>
                            {group.options?.map(opt => {
                                const isSelected = group.selection_type === 'single' 
                                    ? selectedModifiers[group.id] === opt.id 
                                    : (selectedModifiers[group.id] || []).includes(opt.id);

                                return (
                                    <Grid item xs={6} sm={4} key={opt.id}>
                                        <Box 
                                            onClick={() => handleModifierToggle(group.id, opt.id, group.selection_type)}
                                            sx={{ 
                                                p: 1.5, borderRadius: 2, border: '1px solid', textAlign: 'center', cursor: 'pointer',
                                                borderColor: isSelected ? '#0ea5e9' : '#cbd5e1',
                                                bgcolor: isSelected ? '#f0f9ff' : 'white',
                                                transition: '0.2s'
                                            }}
                                        >
                                            <Typography variant="body2" fontWeight="600" color={isSelected ? '#0369a1' : '#475569'}>
                                                {opt.name}
                                            </Typography>
                                            {opt.price > 0 && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    +${parseFloat(opt.price).toFixed(2)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'space-between', bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <IconButton onClick={() => setQuantity(Math.max(1, quantity - 1))} sx={{ borderRadius: 2 }}><FontAwesomeIcon icon={faMinus} size="sm" /></IconButton>
                    <Typography fontWeight="800" sx={{ px: 2, minWidth: '40px', textAlign: 'center' }}>{quantity}</Typography>
                    <IconButton onClick={() => setQuantity(quantity + 1)} sx={{ borderRadius: 2 }}><FontAwesomeIcon icon={faPlus} size="sm" /></IconButton>
                </Box>
                
                <Button 
                    variant="contained" 
                    onClick={() => onAddToCart(product, selectedOptions, selectedModifiers, quantity, currentPrice)}
                    sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: '700', bgcolor: '#0ea5e9' }}
                >
                    Add - ${(currentPrice * quantity).toFixed(2)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}